import { analytics } from "@/config/analytics";
import { CART_ORIGIN } from "@/constants";
import {
  POST_PURCHASE_NEXT_STEPS_TYPES,
  SKUS,
  VARIANT_IDS,
} from "@/domains/surveys/models/constants";
import type {
  PostPurchaseRecommendationsDataModel,
  PostPurchaseSurveyParamsModel,
} from "@/domains/surveys/models/types";
import { Button, Card, Heading, icons } from "@/ui";
import { buildAddToCartCartUrl } from "@/utils/shopify";
import { ContactBar } from "../../-components";

const NEXT_STEP_TYPE_TO_ICON = {
  [POST_PURCHASE_NEXT_STEPS_TYPES.ADVICE]: icons.AdviceIcon,
  [POST_PURCHASE_NEXT_STEPS_TYPES.ALERT]: icons.WarningIcon,
} as const;

type ReOrderProductsCardProps = {
  surveyParams: PostPurchaseSurveyParamsModel;
  recommendations: PostPurchaseRecommendationsDataModel;
};

export const ReOrderProductsCard = ({
  recommendations,
  surveyParams,
}: ReOrderProductsCardProps) => {
  const currentProduct = surveyParams?.findOrderProductBySku(SKUS.GUIDED_RAPID_RELIEF);

  if (!currentProduct) {
    return null;
  }

  return (
    <Card className="flex flex-col">
      <ContactBar />
      <div className="flex flex-col gap-6 px-6 py-7 md:gap-8 md:px-14 md:py-12">
        <Heading className="text-center md:text-left" level={2}>
          {recommendations.buyer.firstName}, here are some next steps to consider.
        </Heading>
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row">
          <img
            alt={currentProduct.name}
            className="mx-auto w-full shrink-0 rounded-lg object-contain md:w-72"
            src={currentProduct.imageUrl}
          />
          <section className="flex-1">
            {recommendations.nextSteps.map((step, index) => {
              const StepTypeIcon = NEXT_STEP_TYPE_TO_ICON[step.type];

              return (
                <div
                  className="flex flex-col gap-3.5 border-b bg-canvas px-6 py-8 first:rounded-t-lg last:rounded-b-lg last:border-b-0"
                  key={index}
                >
                  <div className="flex items-start gap-2">
                    <StepTypeIcon className="shrink-0" />
                    <Heading level={4}>{step.header}</Heading>
                  </div>
                  <p>{step.description}</p>
                </div>
              );
            })}
          </section>
        </div>
        <div className="flex flex-col items-center gap-5">
          <Heading level={3}>Running Low?</Heading>
          <p className="text-center">You&apos;ve unlocked 20% off! Use code RELIEF at checkout.</p>
        </div>
        <Button asChild>
          <a
            className="flex w-full items-center justify-center"
            href={buildAddToCartCartUrl({
              variantId: VARIANT_IDS.GUIDED_RAPID_RELIEF,
              attributes: { origin: CART_ORIGIN.POST_PURCHASE },
            })}
            onClick={() => {
              return analytics.track("post_purchase_reorder_started");
            }}
            rel="noreferrer"
            target="_blank"
          >
            <span>Order Another Inhaler</span>
            <icons.ArrowRightIcon className="h-5" />
          </a>
        </Button>
      </div>
    </Card>
  );
};
